<template>
  <div class="gs-pricing-step">
    <section class="gs-wizard-card gs-pricing-step__summary">
      <div class="gs-pricing-step__summary-head">
        <h2>Price Points</h2>
        <gs-button
          type="button"
          variant="primary"
          size="icon"
          aria-label="Add new price point"
          title="Add new price point"
          @click="createPricePoint"
        >
          <gs-icon name="plus"></gs-icon>
        </gs-button>
      </div>
      <p
        v-if="!localModel.pricing_points.length"
        class="gs-pricing-step__empty"
      >
        Add at least one price point so customers can buy this product.
      </p>
      <div
        v-if="showPricingValidationSummary && pricingValidationIssues.length"
        class="gs-pricing-step__validation"
      >
        <div>
          <strong>Pricing needs attention</strong>
          <p>Fix these fields before this step can be marked complete.</p>
        </div>
        <button
          v-for="issue in pricingValidationIssues"
          :key="`${issue.pricePointId}-${issue.field}`"
          type="button"
          @click="openPricePointEditor(issue.pricePointId)"
        >
          <span>{{ issue.pricePointName }}</span>
          <em>{{ issue.message }}</em>
        </button>
      </div>
      <draggable
        :list="displayList"
        tag="div"
        class="gs-pricing-step__list"
        :class="{ 'is-saving': isSavingOrder }"
        handle=".gs-pricing-step__handle"
        :animation="150"
        :disabled="isSavingOrder"
        :move="canMove"
        ghost-class="is-ghost"
        @start="onDragStart"
        @end="commitOrder"
      >
        <template v-for="item in displayList">
          <div
            v-if="item.__divider"
            v-show="showMoreDivider"
            :key="'divider'"
            class="gs-pricing-step__divider"
            :class="{ 'is-open': moreExpanded }"
          >
            <button
              type="button"
              class="gs-pricing-step__divider-toggle"
              :aria-expanded="moreExpanded ? 'true' : 'false'"
              @click="moreExpanded = !moreExpanded"
            >
              <gs-icon :name="moreExpanded ? 'chevron-up' : 'chevron-down'" size="16"></gs-icon>
              <span>More price points ({{ moreCount }})</span>
            </button>
            <small>All price points stay live for customers.</small>
          </div>
          <article
            v-else
            v-show="isInMainList(item) || moreExpanded"
            :key="item.id || indexOf(item)"
            class="gs-pricing-step__item"
            :class="{ 'is-hidden-point': !isInMainList(item), 'is-pinned': isPinned(item) }"
            :data-price-point-id="item.id"
          >
            <div
              class="gs-pricing-step__row"
              :class="{ 'is-active': expandedPricePointId === (item.id || indexOf(item)) }"
            >
              <span class="gs-pricing-step__handle" title="Drag to reorder" @click.stop>
                <gs-icon name="grip-vertical" size="16"></gs-icon>
              </span>
              <button
                type="button"
                class="gs-pricing-step__row-toggle"
                :aria-expanded="expandedPricePointId === item.id ? 'true' : 'false'"
                :aria-controls="`price-point-details-${item.id}`"
                v-tooltip="descriptionTooltip(pricePointTooltip(item))"
                @click="togglePricePointDetails(item.id || indexOf(item))"
              >
                <span class="gs-pricing-step__name-stack">
                  <strong>{{ item.name }}</strong>
                  <small :title="item.internal_name">{{ item.internal_name || 'No internal description' }}</small>
                  <span class="gs-pricing-step__billing-story">{{ billingStory(item) }}</span>
                </span>
              </button>
              <div class="gs-pricing-step__row-actions">
                <button
                  type="button"
                  class="gs-pricing-step__icon-toggle gs-pricing-step__hover-action"
                  :aria-label="isInMainList(item) ? 'Hide in More price points list' : 'Show in main list'"
                  v-tooltip="descriptionTooltip(isInMainList(item) ? 'Hide in More price points list' : 'Show in main list')"
                  :disabled="isSavingOrder"
                  @click="setVisible(item, !isInMainList(item))"
                ><font-awesome-icon :icon="isInMainList(item) ? eyeIcon : eyeOffIcon" /></button>
                <button
                  type="button"
                  class="gs-pricing-step__icon-toggle gs-pricing-step__hover-action"
                  :class="{ 'is-on': isPinned(item) }"
                  :aria-pressed="isPinned(item) ? 'true' : 'false'"
                  :aria-label="isPinned(item) ? 'Unpin price point' : 'Pin price point to top'"
                  v-tooltip="descriptionTooltip(isPinned(item) ? 'Unpin and restore previous position' : 'Pin to top')"
                  :disabled="isSavingOrder"
                  @click="setPinned(item, !isPinned(item))"
                ><font-awesome-icon :icon="pinIcon" /></button>
                <button
                  type="button"
                  class="gs-pricing-step__icon-toggle gs-pricing-step__expand-toggle"
                  :aria-label="expandedPricePointId === item.id ? 'Collapse price point details' : 'Expand price point details'"
                  :aria-expanded="expandedPricePointId === item.id ? 'true' : 'false'"
                  :aria-controls="`price-point-details-${item.id}`"
                  @click="togglePricePointDetails(item.id || indexOf(item))"
                ><gs-icon :name="expandedPricePointId === item.id ? 'chevron-up' : 'chevron-down'" size="18"></gs-icon></button>
              </div>
            </div>
            <div
              v-if="expandedPricePointId === (item.id || indexOf(item))"
              :id="`price-point-details-${item.id}`"
              class="gs-pricing-step__details"
            >
              <div class="gs-pricing-step__details-head">
                <span>Price point details</span>
                <div class="gs-pricing-step__details-tools">
                  <div class="gs-pricing-step__overflow" @click.stop>
                    <gs-button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Price point actions"
                      @click="toggleExpandedPricePointActions(item.id || indexOf(item))"
                    >
                      <gs-icon name="ellipsis"></gs-icon>
                    </gs-button>
                    <div
                      v-if="activePricePointActionsId === (item.id || indexOf(item))"
                      class="gs-pricing-step__overflow-menu"
                    >
                      <button type="button" @click="openClonePricePointConfirmModal(indexOf(item))">
                        <gs-icon name="copy" size="15"></gs-icon>
                        Clone
                      </button>
                      <button
                        type="button"
                        :disabled="!isDeletablePricingOption(indexOf(item))"
                        @click="openPricePointDeleteConfirm(indexOf(item))"
                      >
                        <gs-icon name="trash" size="15"></gs-icon>
                        Delete
                      </button>
                    </div>
                  </div>
                  <div class="gs-pricing-step__detail-actions">
                    <gs-button type="button" @click="openPricePointEditor(item.id)">
                      <gs-icon name="edit"></gs-icon>
                      Edit
                    </gs-button>
                  </div>
                </div>
              </div>
              <div class="gs-pricing-step__payment-summary" v-tooltip="descriptionTooltip(billingStory(item))">
                <small>Payment schedule</small>
                <p>{{ billingStory(item) }}</p>
              </div>
              <div class="gs-pricing-step__detail-summary">
                <div v-tooltip="descriptionTooltip(item.description)">
                  <small>Price point description</small>
                  <p>{{ item.description || 'No description added yet.' }}</p>
                </div>
                <div v-tooltip="descriptionTooltip(item.internal_name)">
                  <small>Internal description</small>
                  <p>{{ item.internal_name || 'Not set' }}</p>
                </div>
                <div class="gs-pricing-step__detail-badges">
                  <span :class="{ 'is-on': item.is_default }">{{ item.is_default ? 'Default checkout option' : 'Not default' }}</span>
                  <span :class="{ 'is-on': item.coupons_enabled }">{{ item.coupons_enabled ? 'Coupons enabled' : 'Coupons off' }}</span>
                </div>
              </div>
              <div class="gs-pricing-step__detail-grid">
                <span>
                  <small>Billing model</small>
                  <strong>{{ billingLabel(item) }}</strong>
                </span>
                <span>
                  <small>Trial</small>
                  <strong>{{ pricePointTrialLabel(item) }}</strong>
                </span>
                <span>
                  <small>Price</small>
                  <strong>{{ pricePointAmountLabel(item) }}</strong>
                </span>
                <span>
                  <small>Currency</small>
                  <strong>{{ item.currency_code || 'USD' }}</strong>
                </span>
                <span>
                  <small>Default</small>
                  <strong>{{ item.is_default ? 'Yes' : 'No' }}</strong>
                </span>
                <span>
                  <small>Coupons</small>
                  <strong>{{ item.coupons_enabled ? 'Enabled' : 'Disabled' }}</strong>
                </span>
              </div>
            </div>
          </article>
        </template>
      </draggable>
    </section>

    <gs-drawer v-model="showPricingEditor" title="Price Point" size="xl">
      <p class="gs-pricing-step__drawer-intro">
        Pricing plans determine how customers will get billed for this product.
      </p>
      <div v-if="selectedPricingPoint" class="gs-pricing-step__drawer-summary">
        <span>
          <small>Selected plan</small>
          <strong v-tooltip="descriptionTooltip(pricePointTooltip(selectedPricingPoint))">{{ selectedPricingPoint.name }}</strong>
        </span>
        <span>
          <small>Type</small>
          <strong>{{ billingLabel(selectedPricingPoint) }}</strong>
        </span>
        <span>
          <small>Price</small>
          <strong>{{ pricePointAmountLabel(selectedPricingPoint) }}</strong>
        </span>
        <label class="gs-pricing-step__summary-toggle">
          <span>
            <small>
              Default
              <info-icon
                class="gs-pricing-step__tooltip-icon"
                v-tooltip="{
                  classes: 'w-80',
                  content: 'The default price point is preselected anywhere this product needs one primary checkout option. If this is not the default, customers can still buy it through a direct link or wherever you choose to offer it.',
                }"
              />
            </small>
            <strong>{{ selectedPricingPoint.is_default ? 'Default plan' : 'Not default' }}</strong>
          </span>
          <button
            type="button"
            class="gs-pricing-step__default-switch"
            :class="{ 'is-on': selectedPricingPoint.is_default }"
            :disabled="isTrialPricePoint(selectedPricingPoint)"
            role="switch"
            :aria-checked="Boolean(selectedPricingPoint.is_default)"
            aria-label="Use this price point as the default checkout option"
            @click="setIsDefaultPricePoint(selectedPricingPointIndex)"
          >
            <span></span>
          </button>
          <em v-if="isTrialPricePoint(selectedPricingPoint)">Trial price points cannot be the default checkout option.</em>
        </label>
      </div>
      <div v-if="selectedPricingPoint" class="gs-pricing-step__payment-summary">
        <small>Payment schedule</small>
        <p>{{ billingStory(selectedPricingPoint) }}</p>
      </div>
      <div class="gs-pricing-editor-form">
        <div v-for="(pricingPoint, index) in localModel.pricing_points" :key="index">
          <div v-if="pricePointsTab === 'price_point_' + pricingPoint.id" :ref="'price_point_tab' + pricingPoint.id">
            <section class="gs-pricing-editor-section">
              <div class="gs-pricing-editor-section__head">
                <h4>Basics</h4>
                <p>Name the offer and describe what customers are buying.</p>
              </div>

              <div class="gs-pricing-editor-grid">
                <div class="gs-pricing-editor-field" v-tooltip="descriptionTooltip(pricingPoint.name)">
                  <label>{{ $t('frontend_price_point_name') }}</label>
                  <text-input v-model="pricingPoint.name" :validator="$v.localModel.pricing_points.$each[index].name"
                    :attribute="$t('frontend_groovesell_name')" value="pricingPoint.name"
                    :placeholder="$t('frontend_product_step2_name')" />
                </div>
                <div class="gs-pricing-editor-field" v-tooltip="descriptionTooltip(pricingPoint.internal_name)">
                  <label>{{ $t('frontend_internal_name') }}</label>
                  <text-area v-model="pricingPoint.internal_name" :validator="$v.localModel.pricing_points.$each[index].internal_name
                    " :attribute="$t('frontend_internal_name')" value="pricingPoint.internal_name"
                    :placeholder="$t('frontend_internal_name')" />
                </div>
              </div>

              <div class="gs-pricing-editor-field" v-tooltip="descriptionTooltip(pricingPoint.description)">
                <label>{{ $t('frontend_product_step2_price_point_description') }}</label>
                <text-area v-model="pricingPoint.description" :validator="$v.localModel.pricing_points.$each[index].description
                  " value="pricingPoint.description" :attribute="$t('frontend_desc')"
                  :placeholder="$t('frontend_product_step2_price_description')" />
              </div>

            </section>

            <section v-if="pricingPoint.type != 5" class="gs-pricing-editor-section">
              <div class="gs-pricing-editor-section__head inline">
                <div>
                  <h4>Trial</h4>
                  <p>Initial payment before regular billing begins.</p>
                </div>
                <gd-toggle aria-label="Enable trial pricing" :value="pricingPoint.is_trial" @change="setIsTrialPricePoint(index)" />
              </div>

              <div v-if="pricingPoint.is_trial" class="gs-pricing-editor-grid two">
                <div class="gs-pricing-editor-field">
                  <label>
                    {{ $t('frontend_product_step2_initial_price') }}
                    <em>(trial price)</em>
                  </label>
                  <gd-currency-input v-model="pricingPoint.trial_price" :currency-code="pricingPoint.currency_code" />
                </div>
                <div class="gs-pricing-editor-field">
                  <label>
                    {{ $t('frontend_product_step2_initial_period') }}
                    <em>(trial length)</em>
                  </label>
                  <div class="gs-pricing-editor-split-input">
                    <text-input v-model="pricingPoint.trial_period" :validator="$v.localModel.pricing_points.$each[index].trial_period
                      " :attribute="$t('frontend_product_step2_period')" value="pricingPoint.trial_period"
                      :placeholder="$t('frontend_product_step2_period')" />
                    <gd-dropdown
                      v-model="pricingPoint.trial_period_type"
                      size="full"
                      :options="trialPeriodOptions"
                    />
                  </div>
                </div>
              </div>
              <div v-else class="gs-pricing-editor-empty-panel">
                <strong>No trial period</strong>
                <p>Turn this on only when buyers should pay an introductory trial price before the normal price begins.</p>
              </div>
            </section>

            <section class="gs-pricing-editor-section">
              <div class="gs-pricing-editor-section__head">
                <h4>Amount</h4>
                <p>{{ pricingPoint.type == 5 ? 'Minimum contribution.' : pricingPoint.is_trial ? 'Regular payment after the trial.' : 'Regular payment amount.' }}</p>
              </div>

              <div class="gs-pricing-editor-field">
                <label>{{
                  pricingPoint.type == 5
                  ? $t('frontend_product_step2_price_minimum')
                  : pricingPoint.is_trial ? 'Amount after trial' : $t('frontend_product_step2_price')
                }}</label>
                <gd-currency-input v-model="pricingPoint.price" :currency-code="pricingPoint.currency_code" />
              </div>
            </section>

            <section class="gs-pricing-editor-section">
              <div class="gs-pricing-editor-section__head">
                <h4>Billing</h4>
                <p>Payment type and schedule.</p>
              </div>

              <div class="gs-pricing-editor-type-select">
                <div class="gs-pricing-editor-field">
                  <label>Billing model</label>
                  <div
                    class="gs-pricing-editor-combobox"
                    @click.stop
                    @keydown.down.prevent.stop="moveBillingModelHighlight(pricingPoint, 1)"
                    @keydown.up.prevent.stop="moveBillingModelHighlight(pricingPoint, -1)"
                    @keydown.enter.prevent.stop="commitBillingModel(pricingPoint)"
                    @keydown.esc.prevent.stop="closeBillingModelMenu"
                  >
                    <button
                      type="button"
                      class="gs-pricing-editor-select"
                      :aria-expanded="isBillingModelOpen(pricingPoint) ? 'true' : 'false'"
                      @click="toggleBillingModelMenu(pricingPoint)"
                    >
                      <span>{{ pricePointTypeLabel(pricingPoint) }}</span>
                      <gs-icon name="chevron-down"></gs-icon>
                    </button>
                    <div
                      v-if="isBillingModelOpen(pricingPoint)"
                      class="gs-pricing-editor-model-menu"
                      role="listbox"
                    >
                      <button
                        v-for="pricePointType in getPricePointTypes"
                        :key="pricePointType.id"
                        type="button"
                        class="gs-pricing-editor-model-option"
                        :class="{
                          'is-highlighted': Number(billingPreviewType(pricingPoint)) === Number(pricePointType.id),
                          'is-selected': Number(pricingPoint.type) === Number(pricePointType.id),
                        }"
                        role="option"
                        :aria-selected="Number(pricingPoint.type) === Number(pricePointType.id) ? 'true' : 'false'"
                        @mouseenter="highlightBillingModel(pricingPoint, pricePointType.id)"
                        @mousedown.prevent="commitBillingModel(pricingPoint, pricePointType.id)"
                      >
                        <span>{{ pricePointType.title }}</span>
                        <small>{{ pricePointType.description }}</small>
                      </button>
                    </div>
                  </div>
                </div>
                <aside class="gs-pricing-editor-type-note">
                  <small>{{ billingPreviewOption(pricingPoint).title }}</small>
                  <strong>{{ pricePointTypeShortCopyForType(billingPreviewOption(pricingPoint).id) }}</strong>
                  <p>{{ billingPreviewOption(pricingPoint).description }}</p>
                </aside>
              </div>

              <div v-if="pricingPoint.type == 2 || pricingPoint.type == 3 || pricingPoint.type == 4" class="gs-pricing-editor-grid two">
                <div class="gs-pricing-editor-field">
                  <label>{{ $t('frontend_product_step2_interval') }}</label>
                  <gd-dropdown
                    v-model="pricingPoint.recurring_interval"
                    size="full"
                    :options="recurringIntervalOptions(pricingPoint.type)"
                  />
                </div>
              </div>

              <div v-if="pricingPoint.type == 3 || pricingPoint.type == 4" class="gs-pricing-editor-grid two">
                <div class="gs-pricing-editor-field">
                  <label>{{ $t('frontend_product_step2_no_of_installments') }}</label>
                  <text-input
                    v-model="pricingPoint.number_of_installments"
                    :validator="$v.localModel.pricing_points.$each[index].number_of_installments"
                    :attribute="$t('frontend_installments')"
                    value="pricingPoint.number_of_installments"
                    :placeholder="$t('frontend_product_step2_no_of_installments')"
                  />
                </div>
                <div v-if="pricingPoint.type == 4" class="gs-pricing-editor-field">
                  <label>{{ $t('frontend_product_step2_recurrence_interval') }}</label>
                  <gd-dropdown
                    v-model="pricingPoint.recurrence_interval"
                    size="full"
                    :options="yearlyRecurringIntervalOptions"
                  />
                </div>
              </div>

              <div class="gs-pricing-editor-option-row">
                <div>
                  <strong>{{ $t('frontend_product_step2_price_coupons_enabled') }}</strong>
                  <span>Allow customers to apply coupon codes at checkout for this price point.</span>
                </div>
                <gd-toggle
                  aria-label="Allow coupon codes for this price point"
                  :value="pricingPoint.coupons_enabled"
                  @change="setCouponsEnabledPricePoint(index)"
                />
              </div>
            </section>
          </div>
        </div>
      </div>
    </gs-drawer>
    <gs-alert-dialog
      v-model="showDeletePricePointConfirm"
      title="Delete price point?"
      :copy="$t('frontend_non_revertable')"
      :confirm-label="$t('frontend_delete_confirm')"
      @confirm="deletePricePoint"
    ></gs-alert-dialog>
    <gs-alert-dialog
      v-model="showClonePricePointConfirm"
      :title="$t('frontend_groovesell_swal_copy_commision_overrides')"
      :copy="$t('frontend_groovesell_swal_copy_commision_overrides_desc')"
      :confirm-label="$t('frontend_groovesell_swal_copy_commision_overrides_btn_confirm')"
      variant="primary"
      @confirm="clonePricePoint"
    ></gs-alert-dialog>
  </div>
</template>

<script>
import {
  required,
  minLength,
  maxLength,
  numeric,
  decimal,
  requiredIf,
  minValue,
} from 'vuelidate/lib/validators';
import Swal from 'sweetalert2';
import axios from 'axios';
import draggable from 'vuedraggable';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faThumbtack, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import PricePoint from '@/models/PricePoint';
import Funnel from '@/models/Funnel';
import InfoIcon from '@/assets/images/icons/InfoIcon.svg';
import {
  groupPricePoints,
  buildOrderPayload,
  buildTogglePayload,
  isInMainList,
  isPricePointPinned,
  orderedPricePoints,
  reorderByIds,
} from '../pricePointGroups';
import { billingLabel, billingStory, priceAmount, trialSummary, pricePointTooltip } from '../pricePointSummary';

const DIVIDER = { __divider: true, id: '__divider__' };

export default {
  name: 'Step2',
  components: {
    InfoIcon,
    draggable,
    FontAwesomeIcon,
  },
  props: {
    model: {
      required: true,
    },
    product: {
      required: true,
    },
    pricePoints: {
      required: true,
    },
  },
  data() {
    return {
      pinIcon: faThumbtack,
      eyeIcon: faEye,
      eyeOffIcon: faEyeSlash,
      pricePointId: null,
      funnel: null,
      pricePointsTab: '',
      localModel: this.model,
      localPricePoints: this.pricePoints,
      pricePointIndex: 0,
      pricePoint: null,
      showPricingEditor: false,
      showDeletePricePointConfirm: false,
      showClonePricePointConfirm: false,
      expandedPricePointId: null,
      activePricePointActionsId: null,
      openBillingModelId: null,
      billingModelPreviewById: {},
      showPricingValidationSummary: false,
      displayList: [],
      moreExpanded: false,
      isSavingOrder: false,
      dragStartIds: [],
    };
  },
  validations: {
    localModel: {
      pricing_points: {
        required,
        minLength: minLength(1),
        $each: {
          name: {
            required,
            minLength: minLength(3),
            maxLength: maxLength(80),
          },
          internal_name: {
            minLength: minLength(3),
            maxLength: maxLength(500),
          },
          description: {
            required,
            minLength: minLength(5),
            maxLength: maxLength(160),
          },
          price: {
            required,
            decimal,
            minValue: minValue(0),
            isValidPricePointPrice(value, object) {
              if (value > 0) {
                return true;
              }
              if (object.is_trial == 1) {
                return false;
              }
              if (object.type == 2 || object.type == 3) {
                return false;
              }
              return true;
            },
          },
          number_of_installments: {
            required: requiredIf((object) => {
              return object.type == 3 || object.type == 4;
            }),
            numeric,
            isValidNumberOfInstallments(value, object) {
              if (object.type == 3 || object.type == 4) {
                if (value < 1) {
                  return false;
                }
                return true;
              }
              return true;
            },
          },

          recurring_interval: {
            required: requiredIf((object) => {
              return object.type == 3 || object.type == 4;
            }),
            numeric,
            isValidNumberOfRicurringInterval(value, object) {
              if (object.type == 3 || object.type == 4) {
                if (value < 1) {
                  return false;
                }
                return true;
              }
              return true;
            },
          },
          recurrence_interval: {
            required: requiredIf((object) => {
              return object.type == 4;
            }),
            numeric,
            isValidNumberOfRecurrenceInterval(value, object) {
              if (object.type == 4) {
                if (value < 1) {
                  return false;
                }
                return true;
              }
              return true;
            },
          },
          trial_price: {
            required: requiredIf((object) => {
              return object.is_trial == 1;
            }),
            decimal,
            minValue: minValue(0),
          },
          trial_period: {
            required: requiredIf((object) => {
              return object.is_trial == 1;
            }),
            numeric,
            minValue: minValue(0),
          },
          $trackBy: 'id',
        },
      },
    },
  },
  computed: {
    trialPeriodOptions() {
      return [
        { value: 1, text: this.$t('frontend_product_step2_days') },
        { value: 2, text: this.$t('frontend_product_step2_weeks') },
        { value: 3, text: this.$t('frontend_product_step2_months') },
        { value: 4, text: this.$t('frontend_product_step2_years') },
      ];
    },
    yearlyRecurringIntervalOptions() {
      return [
        { value: 1, text: this.$t('frontend_product_step2_every_year') },
        { value: 2, text: this.$t('frontend_product_step2_every_2_years') },
      ];
    },
    getPricePointTypes() {
      return [
        {
          id: 1,
          title: this.$t('frontend_product_step_2_pp_types_tab1_title'),
          description: this.$t('frontend_product_step_2_pp_types_tab1_desc'),
        },
        {
          id: 2,
          title: this.$t('frontend_product_step_2_pp_types_tab2_title'),
          description: this.$t('frontend_product_step_2_pp_types_tab2_desc'),
        },
        {
          id: 3,
          title: this.$t('frontend_product_step_2_pp_types_tab3_title'),
          description: this.$t('frontend_product_step_2_pp_types_tab3_desc'),
        },
        {
          id: 4,
          title: this.$t('frontend_product_step_2_pp_types_tab4_title'),
          description: this.$t('frontend_product_step_2_pp_types_tab4_desc'),
          // coming_soon: true
        },
        {
          id: 5,
          title: this.$t('frontend_product_step_2_pp_types_tab5_title'),
          description: this.$t('frontend_product_step_2_pp_types_tab5_desc'),
          // coming_soon: true
        }
      ]
    },
    selectedPricingPoint() {
      const selectedId = this.pricePointsTab.replace('price_point_', '');

      return this.localModel.pricing_points.find((pricingPoint) => {
        return String(pricingPoint.id) === String(selectedId);
      });
    },
    selectedPricingPointIndex() {
      const selectedId = this.pricePointsTab.replace('price_point_', '');

      return this.localModel.pricing_points.findIndex((pricingPoint) => {
        return String(pricingPoint.id) === String(selectedId);
      });
    },
    pricingValidationIssues() {
      if (
        !this.$v ||
        !this.$v.localModel ||
        !this.$v.localModel.pricing_points ||
        !this.$v.localModel.pricing_points.$each
      ) {
        return [];
      }

      return this.localModel.pricing_points.reduce((issues, pricingPoint, index) => {
        return issues.concat(this.getPricePointValidationIssues(pricingPoint, index));
      }, []);
    },
    dividerIndex() {
      return this.displayList.findIndex(item => item.__divider);
    },
    moreCount() {
      const dividerAt = this.dividerIndex;

      return dividerAt === -1 ? 0 : this.displayList.length - dividerAt - 1;
    },
    showMoreDivider() {
      return this.moreCount > 0 || this.localModel.pricing_points.length > 1;
    },
  },
  watch: {
    localModel: {
      deep: true,
      handler() {
        this.$emit('updateModel', this.localModel);
      },
    },

    localPricePoints: {
      deep: true,
      handler() {
        this.$emit('updatePricePoints', this.localPricePoints);
      },
    },

    'localModel.pricing_points': {
      deep: false,
      immediate: true,
      handler() {
        this.rebuildDisplayList();
      },
    },
  },
  mounted() {
    if (
      this.pricePointsTab == '' &&
      this.localModel.pricing_points.length > 0
    ) {
      this.pricePointsTab = `price_point_${this.localModel.pricing_points[0].id}`;
    }
    document.addEventListener('click', this.closeBillingModelMenu);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeBillingModelMenu);
  },
  methods: {
    billingLabel,
    billingStory,
    pricePointTooltip,
    isInMainList,
    descriptionTooltip(content) {
      return { content: content || '', html: false, classes: 'gs-price-point-tooltip', trigger: 'hover focus' };
    },
    rebuildDisplayList() {
      const { pinned, more } = groupPricePoints(this.localModel.pricing_points);
      this.displayList = pinned
        .map(entry => entry.pricePoint)
        .concat([DIVIDER], more.map(entry => entry.pricePoint));
    },

    indexOf(item) {
      return this.localModel.pricing_points.indexOf(item);
    },

    isPinned(pricePoint) {
      return isPricePointPinned(pricePoint);
    },

    canMove(evt) {
      const dragged = evt.draggedContext.element;
      const related = evt.relatedContext.element;
      if (!dragged || dragged.__divider) return false;
      return !related || this.isPinned(dragged) === this.isPinned(related);
    },

    onDragStart() {
      this.activePricePointActionsId = null;
      this.dragStartIds = this.displayList.map(item => String(item.id));
    },

    splitDisplayList(excluded) {
      const dividerAt = this.displayList.findIndex(item => item.__divider);
      const keep = item => !item.__divider && item !== excluded;

      return {
        pinned: this.displayList.slice(0, dividerAt).filter(keep),
        more: this.displayList.slice(dividerAt + 1).filter(keep),
      };
    },

    async commitOrder() {
      const ids = this.displayList.map(item => String(item.id));
      const unchanged =
        ids.length === this.dragStartIds.length &&
        ids.every((id, position) => id === this.dragStartIds[position]);
      this.dragStartIds = [];

      if (unchanged) {
        return;
      }

      const { pinned, more } = this.splitDisplayList(null);
      const previousMoreExpanded = this.moreExpanded;

      if (more.length) {
        this.moreExpanded = true;
      }

      await this.persistOrder(buildOrderPayload(pinned, more), previousMoreExpanded);
    },

    async setPinned(pricePoint, pinnedFlag) {
      if (this.isSavingOrder) return;
      this.activePricePointActionsId = null;
      await this.persistOrder(buildTogglePayload(this.localModel.pricing_points, pricePoint.id, pinnedFlag ? 'pin' : 'unpin'));
    },

    async setVisible(pricePoint, visible) {
      if (this.isSavingOrder) return;
      this.activePricePointActionsId = null;
      const previousMoreExpanded = this.moreExpanded;
      if (!visible) this.moreExpanded = true;
      await this.persistOrder(buildTogglePayload(this.localModel.pricing_points, pricePoint.id, visible ? 'show' : 'hide'), previousMoreExpanded);
    },

    async persistOrder(payload, moreExpandedBeforeChange) {
      if (this.isSavingOrder) {
        this.rebuildDisplayList();
        return;
      }

      const previousPlain = this.localModel.pricing_points.slice();
      const previousModels = this.localPricePoints.slice();
      const previousPlainFlags = this.snapshotOrderFlags(previousPlain);
      const previousModelFlags = this.snapshotOrderFlags(previousModels);
      const previousMoreExpanded = moreExpandedBeforeChange === undefined
        ? this.moreExpanded
        : moreExpandedBeforeChange;

      this.$emit('orderPersistStart');

      payload.items.forEach((entry) => {
        const plain = this.localModel.pricing_points.find(
          p => String(p.id) === String(entry.id)
        );
        const model = this.localPricePoints.find(
          p => String(p.id) === String(entry.id)
        );

        if (plain) {
          this.$set(plain, 'is_pinned', entry.is_pinned);
          this.$set(plain, 'pin_priority', entry.pin_priority);
          this.$set(plain, 'sort_order', entry.sort_order);
        }
        if (model) {
          this.$set(model, 'is_pinned', entry.is_pinned);
          this.$set(model, 'pin_priority', entry.pin_priority);
          this.$set(model, 'sort_order', entry.sort_order);
        }
      });

      const ids = orderedPricePoints(previousPlain).map(entry => entry.id);
      this.localModel.pricing_points.splice(
        0,
        this.localModel.pricing_points.length,
        ...reorderByIds(previousPlain, ids)
      );
      this.localPricePoints.splice(
        0,
        this.localPricePoints.length,
        ...reorderByIds(previousModels, ids)
      );
      this.rebuildDisplayList();

      this.isSavingOrder = true;

      try {
        await axios.put(`products/${this.product.id}/price-points/order`, payload);
        this.$emit('updatePricePoints', this.localPricePoints);
      } catch (error) {
        this.revertOrder(this.localModel.pricing_points, previousPlain, previousPlainFlags);
        this.revertOrder(this.localPricePoints, previousModels, previousModelFlags);
        this.moreExpanded = previousMoreExpanded;
        this.rebuildDisplayList();
        this.$gsToast.error('Could not save the price point order. Please try again.');
      } finally {
        this.isSavingOrder = false;
        this.rebuildDisplayList();
        this.$emit('orderPersistEnd');
      }
    },

    revertOrder(list, previousList, previousFlags) {
      const knownIds = new Set(previousList.map(pricePoint => String(pricePoint.id)));
      const added = list.filter(pricePoint => !knownIds.has(String(pricePoint.id)));
      list.splice(0, list.length, ...previousList.concat(added));
      this.restoreOrderFlags(list, previousFlags);
    },

    snapshotOrderFlags(list) {
      const flagsById = {};

      list.forEach((pricePoint) => {
        flagsById[String(pricePoint.id)] = {
          is_pinned: pricePoint.is_pinned,
          pin_priority: pricePoint.pin_priority,
          sort_order: pricePoint.sort_order,
        };
      });

      return flagsById;
    },

    restoreOrderFlags(list, flagsById) {
      list.forEach((pricePoint) => {
        const saved = flagsById[String(pricePoint.id)];

        if (!saved) {
          return;
        }

        this.$set(pricePoint, 'is_pinned', saved.is_pinned);
        this.$set(pricePoint, 'pin_priority', saved.pin_priority);
        this.$set(pricePoint, 'sort_order', saved.sort_order);
      });
    },

    pricePointKey(pricingPoint) {
      if (!pricingPoint) {
        return '';
      }

      return String(pricingPoint.id || pricingPoint.name || 'new-price-point');
    },

    billingPreviewType(pricingPoint) {
      const key = this.pricePointKey(pricingPoint);

      return this.billingModelPreviewById[key] || pricingPoint.type || 1;
    },

    billingPreviewOption(pricingPoint) {
      const previewType = Number(this.billingPreviewType(pricingPoint));
      const type = this.getPricePointTypes.find((pricePointType) => {
        return Number(pricePointType.id) === previewType;
      });

      return type || this.getPricePointTypes[0];
    },

    isBillingModelOpen(pricingPoint) {
      return this.openBillingModelId === this.pricePointKey(pricingPoint);
    },

    openBillingModelMenu(pricingPoint) {
      const key = this.pricePointKey(pricingPoint);
      this.openBillingModelId = key;
      this.$set(this.billingModelPreviewById, key, Number(pricingPoint.type || 1));
    },

    closeBillingModelMenu() {
      this.openBillingModelId = null;
    },

    toggleBillingModelMenu(pricingPoint) {
      if (this.isBillingModelOpen(pricingPoint)) {
        this.closeBillingModelMenu();
        return;
      }

      this.openBillingModelMenu(pricingPoint);
    },

    highlightBillingModel(pricingPoint, typeId) {
      this.openBillingModelMenu(pricingPoint);
      this.$set(this.billingModelPreviewById, this.pricePointKey(pricingPoint), Number(typeId));
    },

    moveBillingModelHighlight(pricingPoint, direction) {
      if (!this.isBillingModelOpen(pricingPoint)) {
        this.openBillingModelMenu(pricingPoint);
      }

      const options = this.getPricePointTypes;
      const currentType = Number(this.billingPreviewType(pricingPoint));
      const currentIndex = Math.max(
        0,
        options.findIndex((pricePointType) => Number(pricePointType.id) === currentType)
      );
      const nextIndex = (currentIndex + direction + options.length) % options.length;
      this.highlightBillingModel(pricingPoint, options[nextIndex].id);
    },

    commitBillingModel(pricingPoint, typeId = this.billingPreviewType(pricingPoint)) {
      this.setPricePointType(pricingPoint, Number(typeId));
      this.$set(this.billingModelPreviewById, this.pricePointKey(pricingPoint), Number(typeId));
      this.closeBillingModelMenu();
    },

    pricePointTypeLabel(pricingPoint) {
      const type = this.getPricePointTypes.find((pricePointType) => {
        return Number(pricePointType.id) === Number(pricingPoint.type);
      });

      return type ? type.title : 'One-time';
    },

    pricePointTypeDescription(pricingPoint) {
      const type = this.getPricePointTypes.find((pricePointType) => {
        return Number(pricePointType.id) === Number(pricingPoint.type);
      });

      return type ? type.description : 'Charge customers once for this price point.';
    },

    pricePointTypeShortCopy(pricingPoint) {
      return this.pricePointTypeShortCopyForType(pricingPoint.type);
    },

    pricePointTypeShortCopyForType(type) {
      const copyByType = {
        1: 'One checkout payment.',
        2: 'Repeat billing at a regular interval.',
        3: 'A fixed number of payments.',
        4: 'Repeat billing with installment-style payments.',
        5: 'Customer chooses an allowed amount.',
      };

      return copyByType[type] || copyByType[1];
    },

    pricePointTrialLabel(pricingPoint) {
      return trialSummary(pricingPoint);
    },

    isTrialPricePoint(pricingPoint) {
      return pricingPoint && Number(pricingPoint.is_trial) === 1;
    },

    pricePointAmountLabel(pricingPoint) {
      return priceAmount(pricingPoint);
    },

    recurringIntervalOptions(type) {
      const options = [
        { value: 1, text: this.$t('frontend_product_step2_weekly') },
        { value: 2, text: this.$t('frontend_product_step2_every_2_weeks') },
        { value: 3, text: this.$t('frontend_product_step2_monthly') },
        { value: 4, text: this.$t('frontend_product_step2_every_2_months') },
      ];

      if (type != 4) {
        options.push(
          { value: 5, text: this.$t('frontend_product_step2_quarterly') },
          { value: 9, text: this.$t('frontend_product_step2_every_4_months') },
          { value: 6, text: this.$t('frontend_product_step2_half_yearly') },
          { value: 7, text: this.$t('frontend_product_step2_yearly') },
          { value: 8, text: this.$t('frontend_product_step2_every_2_years') }
        );
      }

      return options;
    },
    validate() {
      this.$v.$touch();
      const isValid = !this.$v.$invalid;
      this.showPricingValidationSummary = !isValid;
      if (!isValid && this.pricingValidationIssues.length) {
        this.expandedPricePointId = this.pricingValidationIssues[0].pricePointId;
      }
      this.$emit('on-validate', isValid);
      return isValid;
    },

    getPricePointValidationIssues(pricingPoint, index) {
      const validator = this.$v.localModel.pricing_points.$each[index];
      const pricePointName = pricingPoint.name || `Price point ${index + 1}`;
      const pricePointId = pricingPoint.id || index;
      const issues = [];

      const addIssue = (field, message) => {
        issues.push({
          field,
          message,
          pricePointId,
          pricePointName,
        });
      };

      if (!validator) {
        return issues;
      }

      if (validator.name && validator.name.$invalid) {
        addIssue('name', 'Name must be 3 to 80 characters.');
      }
      if (validator.internal_name && validator.internal_name.$invalid) {
        addIssue('internal_name', 'Internal name must be 3 to 500 characters when used.');
      }
      if (validator.description && validator.description.$invalid) {
        addIssue('description', 'Description must be 5 to 160 characters.');
      }
      if (validator.price && validator.price.$invalid) {
        addIssue('price', 'Price must be valid for this billing model.');
      }
      if (validator.number_of_installments && validator.number_of_installments.$invalid) {
        addIssue('number_of_installments', 'Installments must be at least 1.');
      }
      if (validator.recurring_interval && validator.recurring_interval.$invalid) {
        addIssue('recurring_interval', 'Choose a recurring interval.');
      }
      if (validator.recurrence_interval && validator.recurrence_interval.$invalid) {
        addIssue('recurrence_interval', 'Choose a recurrence interval.');
      }
      if (validator.trial_price && validator.trial_price.$invalid) {
        addIssue('trial_price', 'Trial price is required when trial is enabled.');
      }
      if (validator.trial_period && validator.trial_period.$invalid) {
        addIssue('trial_period', 'Trial length is required when trial is enabled.');
      }

      return issues;
    },

    nextPricePointSortOrder() {
      const existing = (this.localModel.pricing_points || []).map(
        p => Number(p.sort_order) || 0
      );
      return Math.max(0, ...existing) + 1;
    },

    async createPricePoint() {
      this.$loader.start('createPricePoint');

      const newPricePointCount = this.localModel.pricing_points.length + 1;

      const commision_id = Math.ceil(Math.random() * 10000);

      const nextSortOrder = this.nextPricePointSortOrder();

      this.pricePoint = new PricePoint({
        type: 1,
        name: `${this.product.name} #${newPricePointCount}`,
        internal_name: '',
        description: '',
        price: 1,
        recurring_interval: 1,
        number_of_installments: 1,
        recurrence_interval: 1,
        coupons_enabled:1,
        is_trial: 0,
        trial_price: 0.0,
        trial_period: 0,
        trial_period_type: 1,
        salespage_type: 1,
        salespage_details: {
          sales_page: '',
        },
        quantity_enable: 0,
        minimum_quantity: 1,
        maximum_quantity: 5,
        offer_bump_enable: 0,
        offer_bump_options: [],
        post_checkout_type: 1,
        post_checkout_details: {
          thankyou_page: '',
        },
        post_transaction_details: {
          type: 1,
          post_transaction_url: '',
        },
        post_sale_notification_type: 1,
        post_sale_details: null,
        post_sale_integrations: null,
        memberships: null,
        file_memberships: null,
        legal_options: {
          enabled: 0,
          html: '',
        },
        abandon_cart_integrations: null,
        affiliate_registration_integrations: null,
        customer_portal_details: null,
        content_access_type: 0,
        content_access_options: {},
        facebook_conversion_api_enabled: 0,
        pixel_id: 0,
        advanced_tracking_code_enabled: 0,
        advanced_tracking_code_details: {
          tracking_script: '',
        },
        pricing_string: this.$t('frontend_product_step_2_pricing_string'),
        commission_type: 1,
        commission_details: [
          {
            id: commision_id,
            type: 1,
            name: 'Default',
            assigned_to: null,
            sales_commission: {
              commission: 0,
              commission_type: 'percent',
              jv_broker_commission: 0,
              jv_broker_commission_type: 'percent',
            },
            trial_commission: {
              commission: 0,
              commission_type: 'percent',
              jv_broker_commission: 0,
              jv_broker_commission_type: 'percent',
            },
            rebill_commissions: [
              {
                commission: 0,
                no_of_payments: null,
                commission_type: 'percent',
                jv_broker_commission: 0,
                jv_broker_commission_type: 'percent',
              },
            ],
          },
        ],
        is_default: 0,
        is_pinned: true,
        pin_priority: 0,
        sort_order: nextSortOrder,
        post_sale_order_bump_options: {
          post_order_heading_line: null,
          post_order_subheading: null,
          post_order_description: null,
          post_order_video_details: {
            post_order_show_video: 0,
            url: '',
            id: null,
            provider: '',
            permalink: null,
          },
          post_order_bump_enable: 0,
          post_order_display_thumbnail: 0,
          post_order_thumbnail_image: '',
          post_order_is_square: 0,
          post_order_thumbnail_size: 'md',
          post_order_style_id: 1,
          post_order_product_id: null,
          post_order_price_point_id: null,
        },
      }).for(this.product);

      const savedProductData = await this.pricePoint.save();

      this.pricePoint = await this.product
        .pricepoints()
        .$find(savedProductData.data.id);
      this.product.price_points.push(this.pricePoint);
      this.localPricePoints.push(this.pricePoint);
      this.funnel = await Funnel.$find(this.$route.params.id);
      const funnelProducts = await this.funnel.products().$get();
      const productPricePoints = funnelProducts[0].price_points;

      this.localModel.pricing_points.push({
        id: this.pricePoint.id,
        type: 1,
        name: `${this.product.name} #${newPricePointCount}`,
        internal_name: '',
        description: '',
        price: 1,
        recurring_interval: 1,
        number_of_installments: 1,
        recurrence_interval: 1,
        coupons_enabled:1,
        is_trial: 0,
        trial_price: 0.0,
        trial_period: 0,
        trial_period_type: 1,
        salespage_type: 1,
        salespage_details: {
          sales_page: '',
        },
        funnelProducts,
        productPricePoint: productPricePoints,
        minimum_quantity: 1,
        maximum_quantity: 5,
        quantity_enable: 0,
        offer_bump_enable: 0,
        offer_bump_options: [],
        post_checkout_type: 1,
        post_checkout_details: {
          thankyou_page: '',
        },
        post_transaction_details: {
          type: 1,
          post_transaction_url: '',
        },
        post_sale_notification_type: 1,
        post_sale_details: null,
        post_sale_integrations: null,
        memberships: null,
        file_memberships: null,
        legal_options: {
          enabled: 0,
          html: '',
        },
        abandon_cart_integrations: null,
        affiliate_registration_integrations: null,
        customer_portal_details: null,
        content_access_type: 0,
        content_access_options: {},
        facebook_conversion_api_enabled: 0,
        pixel_id: 0,
        advanced_tracking_code_enabled: 0,
        advanced_tracking_code_details: {
          tracking_script: '',
        },
        pricing_string: this.$t('frontend_product_step_2_pricing_string'),
        currency_code: this.funnel.currency_code.toUpperCase(),
        commission_type: 1,
        commission_details: [
          {
            type: 1,
            id: commision_id,
            name: 'Default',
            assigned_to: null,
            sales_commission: {
              commission: 0,
              commission_type: 'percent',
              jv_broker_commission: 0,
              jv_broker_commission_type: 'percent',
            },
            trial_commission: {
              commission: 0,
              commission_type: 'percent',
              jv_broker_commission: 0,
              jv_broker_commission_type: 'percent',
            },
            rebill_commissions: [
              {
                commission: 0,
                no_of_payments: null,
                commission_type: 'percent',
                jv_broker_commission: 0,
                jv_broker_commission_type: 'percent',
              },
            ],
          },
        ],
        is_default: 0,
        is_pinned: true,
        pin_priority: 0,
        sort_order: nextSortOrder,
        post_sale_order_bump_options: {
          post_order_heading_line: null,
          post_order_subheading: null,
          post_order_description: null,
          post_order_video_details: {
            post_order_show_video: 0,
            url: '',
            id: null,
            provider: '',
            permalink: null,
          },
          post_order_bump_enable: 0,
          post_order_display_thumbnail: 0,
          post_order_thumbnail_image: '',
          post_order_is_square: 0,
          post_order_thumbnail_size: 'md',
          post_order_style_id: 1,
          post_order_product_id: null,
          post_order_price_point_id: null,
        },
      });

      this.pricePointsTab = `price_point_${this.pricePoint.id}`;
      this.showPricingEditor = true;
      this.$loader.end('createPricePoint');
    },
    openConfirmModal(id) {
      this.activePricePointActionsId = null;
      this.showDeletePricePointConfirm = true;
      this.pricePointId = id;
    },
    async deletePricePoint() {
      this.$loader.start('deletePricePoint');

      const currentlySelectedPricePoint = this.localPricePoints[this.pricePointId];

      const pricePoint = await this.product
        .pricepoints()
        .$find(currentlySelectedPricePoint.id);
      await pricePoint.delete();

      const deletedId = String(currentlySelectedPricePoint.id);
      const localIndex = this.localPricePoints.findIndex(
        p => String(p.id) === deletedId
      );
      if (localIndex > -1) {
        this.localPricePoints.splice(localIndex, 1);
      }
      const plainIndex = this.localModel.pricing_points.findIndex(
        p => String(p.id) === deletedId
      );
      if (plainIndex > -1) {
        this.localModel.pricing_points.splice(plainIndex, 1);
      }

      let nextOpenIndex = 0;
      if (this.pricePointId == 0) {
        nextOpenIndex = 0;
      } else {
        nextOpenIndex = this.pricePointId - 1;
      }

      if (this.localModel.pricing_points.length) {
        const nextOpenPricePointId =
          this.localModel.pricing_points[nextOpenIndex].id;
        this.pricePointsTab = `price_point_${nextOpenPricePointId}`;
        this.expandedPricePointId = nextOpenPricePointId;
      } else {
        this.pricePointsTab = '';
        this.expandedPricePointId = null;
        this.showPricingEditor = false;
      }
      this.$loader.end('deletePricePoint');
    },
    openClonePricePointConfirmModal(index) {
      this.activePricePointActionsId = null;
      this.pricePointId = index;
      this.showClonePricePointConfirm = true;
    },
    async clonePricePoint() {
      this.$loader.start('clonePricePoint');
      const currentSelectedPricePointIndex = this.localPricePoints[this.pricePointId];
      const currentSelectedPricePoint = await this.product
        .pricepoints()
        .$find(currentSelectedPricePointIndex.id);

      const commissionDetails = [];

      currentSelectedPricePoint.commission_details.forEach((element) => {
        const commision_id = Math.ceil(Math.random() * 10000);
        const tempCommission = {
          id: commision_id,
          type: element.type,
          name: element.name,
          assigned_to: element.assigned_to,
          sales_commission: element.sales_commission,
          trial_commission: element.trial_commission,
          rebill_commissions: element.rebill_commissions,
        };
        commissionDetails.push(tempCommission);
      });

        const newClonedPricePoint = JSON.parse(
          JSON.stringify(currentSelectedPricePoint)
        );
        delete newClonedPricePoint.id;
        newClonedPricePoint.name += ' Clone';
        newClonedPricePoint.commission_details = commissionDetails;
        newClonedPricePoint.is_default = 0;

        const nextSortOrder = this.nextPricePointSortOrder();
        newClonedPricePoint.is_pinned = true;
        newClonedPricePoint.pin_priority = 0;
        newClonedPricePoint.sort_order = nextSortOrder;

        this.pricePoint = new PricePoint(newClonedPricePoint).for(this.product);

        const savedProductData = await this.pricePoint.save();

        this.pricePoint = await this.product
          .pricepoints()
          .$find(savedProductData.data.id);

        this.product.price_points.push(this.pricePoint);
        this.localPricePoints.push(this.pricePoint);
        this.funnel = await Funnel.$find(this.$route.params.id);
        const funnelProducts = await this.funnel.products().$get();
        const productPricePoints = funnelProducts[0].price_points;

        this.localModel.pricing_points.push({
          id: this.pricePoint.id,
          type: this.pricePoint.type,
          name: this.pricePoint.name,
          internal_name: this.pricePoint.internal_name,
          description: this.pricePoint.description,
          price: this.pricePoint.price,
          recurring_interval: this.pricePoint.recurring_interval,
          number_of_installments: this.pricePoint.number_of_installments,
          recurrence_interval: this.pricePoint.recurrence_interval,
          coupons_enabled: this.pricePoint.coupons_enabled,
          is_trial: this.pricePoint.is_trial,
          trial_price: this.pricePoint.trial_price,
          trial_period: this.pricePoint.trial_period,
          trial_period_type: this.pricePoint.trial_period_type,
          salespage_type: this.pricePoint.salespage_type,
          salespage_details: this.pricePoint.salespage_details,
          funnelProducts,
          productPricePoint: productPricePoints,
          minimum_quantity: this.pricePoint.minimum_quantity,
          maximum_quantity: this.pricePoint.maximum_quantity,
          quantity_enable: this.pricePoint.quantity_enable,
          offer_bump_enable: this.pricePoint.offer_bump_enable,
          offer_bump_options: this.pricePoint.offer_bump_options,
          post_checkout_type: this.pricePoint.post_checkout_type,
          post_checkout_details: this.pricePoint.post_checkout_details,
          post_transaction_details: this.pricePoint.post_transaction_details,
          post_sale_notification_type:
            this.pricePoint.post_sale_notification_type,
          post_sale_details: this.pricePoint.post_sale_details,
          post_sale_integrations: this.pricePoint.post_sale_integrations,
          memberships: this.pricePoint.memberships,
          file_memberships: this.pricePoint.file_memberships,
          legal_options: this.pricePoint.legal_options,
          abandon_cart_integrations: this.pricePoint.abandon_cart_integrations,
          affiliate_registration_integrations:
            this.pricePoint.affiliate_registration_integrations,
          customer_portal_details: this.pricePoint.customer_portal_details,
          content_access_type: this.pricePoint.content_access_type,
          content_access_options: this.pricePoint.content_access_options,
          advanced_tracking_code_enabled:
            this.pricePoint.advanced_tracking_code_enabled,
          facebook_conversion_api_enabled:
            this.pricePoint.facebook_conversion_api_enabled,
          pixel_id:
            this.pricePoint.pixel_id,
          advanced_tracking_code_details:
            this.pricePoint.advanced_tracking_code_details,
          pricing_string: this.pricePoint.pricing_string,
          currency_code: this.pricePoint.currency_code,
          commission_type: this.pricePoint.commission_type,
          commission_details: commissionDetails,
          is_default: this.pricePoint.is_default,
          is_pinned: true,
          pin_priority: 0,
          sort_order: nextSortOrder,
          post_sale_order_bump_options:
            this.pricePoint.post_sale_order_bump_options,
        });

        this.pricePointsTab = `price_point_${this.pricePoint.id}`;
        this.expandedPricePointId = this.pricePoint.id;
        this.showPricingEditor = true;
        this.$loader.end('clonePricePoint');
    },

    setIsDefaultPricePoint(index) {
      const pricePoint = this.localModel.pricing_points[index];

      if (!this.isTrialPricePoint(pricePoint)) {
        this.localModel.pricing_points.forEach((pricingPoint) => {
          pricingPoint.is_default = false;
        });

        pricePoint.is_default = !pricePoint.is_default;
      } else {
        return false;
      }
    },

    setIsTrialPricePoint(index) {
      this.localModel.pricing_points[index].is_trial =
        !this.localModel.pricing_points[index].is_trial;
      this.localPricePoints[index].is_trial =
        !this.localPricePoints[index].is_trial;
    },
    setCouponsEnabledPricePoint(index) {
      this.localModel.pricing_points[index].coupons_enabled =
        !this.localModel.pricing_points[index].coupons_enabled;
      this.localPricePoints[index].coupons_enabled =
        !this.localPricePoints[index].coupons_enabled;
    },

    setPricePointType(pricingPoint, type) {
      pricingPoint.type = type;
    },

    openPricePointEditor(pricePointId) {
      this.activePricePointActionsId = null;
      this.pricePointsTab = `price_point_${pricePointId}`;
      this.showPricingEditor = true;
    },

    togglePricePointDetails(pricePointId) {
      this.activePricePointActionsId = null;
      this.expandedPricePointId =
        this.expandedPricePointId === pricePointId ? null : pricePointId;
    },

    toggleExpandedPricePointActions(pricePointId) {
      this.activePricePointActionsId =
        this.activePricePointActionsId === pricePointId ? null : pricePointId;
    },

    openPricePointDeleteConfirm(index) {
      if (!this.isDeletablePricingOption(index)) {
        return;
      }

      this.openConfirmModal(index);
    },

    isDeletablePricingOption(index) {
      return (
        this.localModel.pricing_points.length > 1 &&
        !this.localModel.pricing_points[index].is_default
      );
    },
  },
};
</script>
<style>
.gs-pricing-step {
  display: flex;
  flex-direction: column;
  gap: 18px;
  max-width: 700px;
  margin: 0 auto;
}


.gs-pricing-step__summary-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.gs-pricing-step__summary h2 {
  margin: 0;
  color: var(--gs-foreground);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.gs-pricing-step__summary-head button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  color: var(--gs-primary-foreground);
  border: 0;
  border-radius: 999px;
  background: var(--gs-primary);
  cursor: pointer;
}

.gs-pricing-step__empty {
  margin: 10px 0 0;
  padding: 18px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.45;
  text-align: center;
  background: var(--gs-surface-subtle);
  border: 1px dashed var(--gs-border);
  border-radius: var(--gs-radius-sm);
}

.gs-pricing-step__validation {
  display: grid;
  gap: 10px;
  margin: 14px 0 2px;
  padding: 16px;
  background: var(--gs-primary-soft);
  border: 1px solid var(--gs-primary);
  border-radius: var(--gs-radius-md);
}

.gs-pricing-step__validation strong {
  display: block;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
  line-height: 1.35;
}

.gs-pricing-step__validation p {
  margin: 3px 0 0;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.45;
}

.gs-pricing-step__validation button {
  display: grid;
  grid-template-columns: minmax(130px, .8fr) minmax(0, 1.2fr);
  gap: 10px;
  align-items: center;
  min-height: 42px;
  padding: 9px 12px;
  color: var(--gs-foreground);
  text-align: left;
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  cursor: pointer;
}

.gs-pricing-step__validation button:hover {
  border-color: var(--gs-primary);
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gs-pricing-step__validation button span {
  min-width: 0;
  overflow: hidden;
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gs-pricing-step__validation button em {
  min-width: 0;
  overflow: hidden;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  font-style: normal;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gs-pricing-step__hint {
  margin: 14px 0 0;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.45;
}

.gs-pricing-step__list.is-saving {
  opacity: .65;
  pointer-events: none;
}

.gs-pricing-step__item {
  margin-top: 12px;
}

.gs-pricing-step__item.is-ghost .gs-pricing-step__row {
  opacity: .4;
  background: var(--gs-primary-soft);
}

.gs-pricing-step__item.is-hidden-point .gs-pricing-step__row {
  background: var(--gs-surface-subtle);
}

.gs-pricing-step__divider {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 14px 0;
  padding: 10px 14px;
  border: 1px dashed var(--gs-border-strong);
  border-radius: var(--gs-radius-md);
  background: var(--gs-surface-subtle);
}

.gs-pricing-step__divider-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 650;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.gs-pricing-step__divider small {
  color: var(--gs-muted-foreground);
  font-size: 12px;
}

.gs-pricing-step__row {
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr) auto;
  column-gap: 12px;
  align-items: center;
  width: 100%;
  min-height: 58px;
  padding: 14px 12px;
  color: var(--gs-muted-foreground);
  text-align: left;
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-sm);
  transition: border-color .18s ease, box-shadow .18s ease, background .18s ease;
}

.gs-pricing-step__row.is-active {
  background: var(--gs-active-soft);
  border-color: var(--gs-active);
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
  box-shadow: 0 0 0 3px var(--gs-active-shadow);
}

.gs-pricing-step__name-stack,
.gs-pricing-step__row > span {
  min-width: 0;
}

.gs-pricing-step__name-stack {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.gs-pricing-step__row-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.gs-pricing-step__row-actions { display: flex; gap: 2px; align-items: center; }
.gs-pricing-step__icon-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  color: var(--gs-muted-foreground);
  background: transparent;
  cursor: pointer;
}
.gs-pricing-step__icon-toggle svg { width: 15px; height: 15px; }
.gs-pricing-step__icon-toggle:hover { background: var(--gs-muted); }
.gs-pricing-step__icon-toggle.is-on { color: var(--gs-primary); background: var(--gs-primary-soft); }
.gs-pricing-step__row-toggle:focus-visible,
.gs-pricing-step__icon-toggle:focus-visible { outline: 2px solid var(--gs-primary); outline-offset: 3px; }
.gs-pricing-step__icon-toggle:disabled { cursor: wait; opacity: .6; }

@media (hover: hover) and (pointer: fine) {
  .gs-pricing-step__hover-action:not(.is-on) {
    opacity: 0;
    pointer-events: none;
  }

  .gs-pricing-step__item:hover .gs-pricing-step__hover-action,
  .gs-pricing-step__item:focus-within .gs-pricing-step__hover-action {
    opacity: 1;
    pointer-events: auto;
  }

  .gs-pricing-step__item:hover .gs-pricing-step__hover-action:disabled,
  .gs-pricing-step__item:focus-within .gs-pricing-step__hover-action:disabled {
    opacity: .6;
  }
}

@media (pointer: coarse) {
  .gs-pricing-step__icon-toggle { width: 44px; height: 44px; }
}

.gs-pricing-step__row .gs-pricing-step__handle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  color: var(--gs-muted-foreground);
  cursor: grab;
}

.gs-pricing-step__row .gs-pricing-step__handle:active {
  cursor: grabbing;
}

.gs-pricing-step__name-stack small {
  min-width: 0;
  overflow: hidden;
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-weight: 500;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.gs-pricing-step__row strong,
.gs-pricing-step__row b {
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 600;
}

.gs-pricing-step__row strong,
.gs-pricing-step__row span {
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  line-height: 1.5;
}

.gs-pricing-step__row span {
  color: var(--gs-muted-foreground);
  font-size: 14px;
  font-weight: 400;
}

.gs-pricing-step__row i {
  color: var(--gs-muted-foreground);
  font-size: 15px;
  text-align: right;
}

.gs-pricing-step__details {
  position: relative;
  padding: 18px;
  background: #ffffff;
  border: 1px solid var(--gs-active);
  border-top: 0;
  border-bottom-right-radius: var(--gs-radius-md);
  border-bottom-left-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-sm);
}

.gs-pricing-step__details-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 30px;
  margin-bottom: 14px;
}

.gs-pricing-step__details-tools {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.gs-pricing-step__details-head > span {
  color: var(--gs-muted-foreground);
  font-size: 14px;
  font-weight: 400;
}

.gs-pricing-step__overflow {
  position: relative;
}

.gs-pricing-step__overflow > button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: var(--gs-muted-foreground);
  background: transparent;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
}

.gs-pricing-step__overflow > button:hover {
  color: var(--gs-foreground);
  background: var(--gs-surface-subtle);
}

.gs-pricing-step__overflow-menu {
  position: absolute;
  top: 36px;
  right: 0;
  z-index: 15;
  width: 144px;
  padding: 6px;
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-md);
}

.gs-pricing-step__overflow-menu button {
  display: block;
  width: 100%;
  min-height: 36px;
  padding: 0 11px;
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 400;
  line-height: 36px;
  text-align: left;
  background: transparent;
  border: 0;
  border-radius: var(--gs-radius-sm);
  cursor: pointer;
}

.gs-pricing-step__overflow-menu button:hover {
  color: var(--gs-foreground);
  background: var(--gs-surface-subtle);
}

.gs-pricing-step__overflow-menu button:disabled {
  color: var(--gs-border-strong);
  cursor: not-allowed;
  background: transparent;
}

.gs-pricing-step__detail-summary {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 14px;
  align-items: start;
  padding: 14px;
  background: var(--gs-muted);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-pricing-step__detail-summary small {
  display: block;
  margin-bottom: 6px;
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.gs-pricing-step__detail-summary p {
  margin: 0;
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.45;
}

.gs-pricing-step__detail-badges {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 8px;
}

.gs-pricing-step__detail-badges span {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 10px;
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-weight: 700;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: 999px;
  white-space: nowrap;
}

.gs-pricing-step__detail-badges span.is-on {
  color: var(--gs-success);
  background: var(--gs-success-soft);
  border-color: var(--gs-success-border);
}

.gs-pricing-step__detail-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 14px;
}

.gs-pricing-step__detail-grid > span {
  min-width: 0;
  padding: 12px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-pricing-step__detail-grid small {
  display: block;
  margin-bottom: 6px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  font-weight: 400;
}

.gs-pricing-step__detail-grid strong {
  display: block;
  min-width: 0;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 500;
  white-space: normal;
  overflow-wrap: anywhere;
  line-height: 1.5;
}

.gs-pricing-step__details p {
  margin: 16px 0 0;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.45;
}

.gs-pricing-step__detail-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.gs-pricing-step__detail-actions button {
  min-width: 84px;
  min-height: 38px;
  padding: 0 16px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  font-weight: 500;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  cursor: pointer;
}

.gs-pricing-step__detail-actions button:first-child {
  color: var(--gs-active);
  background: var(--gs-active-soft);
  border-color: var(--gs-active-border);
}

.gs-pricing-step__detail-actions button:hover {
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gs-pricing-step__detail-actions button:disabled {
  color: var(--gs-border-strong);
  border-color: var(--gs-border);
  cursor: not-allowed;
  box-shadow: none;
}

@media (max-width: 700px) {
  .gs-pricing-step__detail-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}


.gs-pricing-step__drawer-intro {
  margin: 0 0 18px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.5;
}

.gs-pricing-step__drawer-summary {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, .9fr) minmax(0, .7fr) minmax(0, 1.1fr);
  gap: 14px;
  margin-bottom: 22px;
  padding: 16px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-pricing-step__drawer-summary small {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  font-weight: 400;
}

.gs-pricing-step__tooltip-icon {
  width: 14px;
  height: 14px;
  color: var(--gs-muted-foreground);
  fill: currentColor;
  cursor: help;
}

.gs-pricing-step__tooltip-icon:hover {
  color: var(--gs-muted-foreground);
}

.gs-pricing-step__drawer-summary strong {
  display: block;
  min-width: 0;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 500;
  white-space: normal;
  overflow-wrap: anywhere;
}

.gs-pricing-step__summary-toggle {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  min-width: 0;
  gap: 12px;
  margin: 0;
}

.gs-pricing-step__summary-toggle span {
  min-width: 0;
}

.gs-pricing-step__summary-toggle em {
  grid-column: 1 / -1;
  display: block;
  margin-top: 4px;
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-style: normal;
  line-height: 1.35;
}

.gs-pricing-step__default-switch {
  position: relative;
  flex: 0 0 auto;
  width: 50px;
  height: 28px;
  padding: 0;
  background: var(--gs-border);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: background .15s ease, opacity .15s ease;
}

.gs-pricing-step__default-switch span {
  position: absolute;
  top: 4px;
  left: 4px;
  width: 20px;
  height: 20px;
  margin: 0;
  background: #ffffff;
  border-radius: 999px;
  box-shadow: 0 2px 5px rgba(31, 37, 72, .16);
  transition: transform .15s ease;
}

.gs-pricing-step__default-switch.is-on {
  background: var(--gs-primary);
}

.gs-pricing-step__default-switch.is-on span {
  transform: translateX(22px);
}

.gs-pricing-step__default-switch:disabled {
  cursor: not-allowed;
  opacity: .55;
}

.gs-pricing-editor-form {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.gs-pricing-editor-form > div {
  display: contents;
}

.gs-pricing-editor-section {
  padding: 20px;
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-pricing-editor-form > div > div { display: grid; gap: 16px; }
.gs-pricing-step__payment-summary {
  padding: 14px 16px;
  margin: 0 0 16px;
  border-left: 3px solid var(--gs-primary);
  background: var(--gs-surface-subtle);
  overflow-wrap: anywhere;
}
.gs-pricing-step__payment-summary small { font-size: 12px; font-weight: 600; color: var(--gs-muted-foreground); }
.gs-pricing-step__payment-summary p { margin: 6px 0 0; font-size: 14px; line-height: 1.6; color: var(--gs-foreground); }
.gs-pricing-step__detail-summary p { margin-top: 0; overflow-wrap: anywhere; }
.tooltip.gs-price-point-tooltip {
  max-width: min(440px, calc(100vw - 32px));
  z-index: 10060;
  pointer-events: none;
}
.gs-price-point-tooltip .tooltip-inner {
  padding: 10px 14px;
  border-radius: 6px;
  background: #172334;
  color: #ffffff;
  font-size: 13px;
  white-space: pre-line;
  overflow-wrap: anywhere;
  text-align: left;
  line-height: 1.6;
}

.gs-pricing-editor-section__head {
  margin-bottom: 18px;
}

.gs-pricing-editor-section__head.inline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.gs-pricing-editor-section__head h4 {
  display: block;
  margin: 0 0 4px;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
  line-height: 1.3;
}

.gs-pricing-editor-section__head p {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.45;
}

.gs-pricing-editor-grid {
  display: grid;
  gap: 18px;
  align-items: start;
}

.gs-pricing-editor-grid.two {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.gs-pricing-editor-field {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-width: 0;
}

.gs-pricing-editor-grid + .gs-pricing-editor-grid,
.gs-pricing-editor-field + .gs-pricing-editor-switches,
.gs-pricing-editor-grid + .gs-pricing-editor-field {
  margin-top: 16px;
}

.gs-pricing-editor-field label,
.gs-pricing-editor-switches label span {
  display: block;
  margin-bottom: 8px;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 600;
}

.gs-pricing-editor-switches {
  display: flex;
  flex-wrap: wrap;
  gap: 22px;
  align-items: center;
  margin-top: 18px;
}

.gs-pricing-editor-switches label {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin: 0;
}

.gs-pricing-editor-switches label span {
  margin-bottom: 0;
}

.gs-pricing-editor-option-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-top: 18px;
  padding: 16px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-pricing-editor-option-row strong {
  display: block;
  margin-bottom: 4px;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
  line-height: 1.35;
}

.gs-pricing-editor-option-row span {
  display: block;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.45;
}

.gs-pricing-editor-type-select {
  display: grid;
  grid-template-columns: minmax(240px, .9fr) minmax(0, 1.1fr);
  gap: 18px;
  align-items: stretch;
}

.gs-pricing-editor-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-height: 46px;
  padding: 0 42px 0 14px;
  color: var(--gs-foreground);
  font-size: 16px;
  font-weight: 500;
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
}

.gs-pricing-editor-combobox {
  position: relative;
}

.gs-pricing-editor-combobox .gs-pricing-editor-select {
  padding-right: 14px;
  cursor: pointer;
}

.gs-pricing-editor-combobox .gs-pricing-editor-select span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gs-pricing-editor-model-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 40;
  width: 100%;
  max-height: 310px;
  padding: 6px;
  overflow: auto;
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-md);
}

.gs-pricing-editor-model-option {
  display: block;
  width: 100%;
  min-height: 58px;
  padding: 10px 12px;
  color: var(--gs-foreground);
  text-align: left;
  background: transparent;
  border: 0;
  border-radius: var(--gs-radius-sm);
  cursor: pointer;
}

.gs-pricing-editor-model-option span {
  display: block;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
}

.gs-pricing-editor-model-option small {
  display: block;
  margin-top: 2px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  font-weight: 400;
  line-height: 1.35;
}

.gs-pricing-editor-model-option.is-highlighted {
  background: var(--gs-active-soft);
}

.gs-pricing-editor-model-option.is-selected {
  box-shadow: inset 3px 0 0 var(--gs-active);
}

.gs-pricing-editor-type-card {
  position: relative;
  min-height: 132px;
  padding: 18px 18px 18px 54px;
  color: var(--gs-muted-foreground);
  text-align: left;
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  cursor: pointer;
  transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
}

.gs-pricing-editor-type-card:hover {
  border-color: var(--gs-border);
  box-shadow: var(--gs-shadow-md);
}

.gs-pricing-editor-type-card.is-selected {
  background: var(--gs-active-soft);
  border-color: var(--gs-active);
  box-shadow: 0 0 0 3px var(--gs-active-shadow);
}

.gs-pricing-editor-type-card > span {
  position: absolute;
  top: 20px;
  left: 18px;
  width: 22px;
  height: 22px;
  border: 2px solid var(--gs-border-strong);
  border-radius: 999px;
}

.gs-pricing-editor-type-card.is-selected > span {
  border-color: var(--gs-active);
}

.gs-pricing-editor-type-card.is-selected > span::after {
  position: absolute;
  top: 4px;
  left: 4px;
  width: 10px;
  height: 10px;
  content: '';
  background: var(--gs-active);
  border-radius: 999px;
}

.gs-pricing-editor-type-card strong {
  display: block;
  margin-bottom: 8px;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
  line-height: 1.25;
}

.gs-pricing-editor-type-card small {
  display: block;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
}

.gs-pricing-editor-type-note {
  padding: 16px 18px;
  background: var(--gs-primary-soft);
  border: 1px solid var(--gs-primary);
  border-radius: var(--gs-radius-sm);
}

.gs-pricing-editor-type-note small {
  display: block;
  margin-bottom: 6px;
  color: var(--gs-primary);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
}

.gs-pricing-editor-type-note strong {
  display: block;
  margin-bottom: 6px;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
  line-height: 1.3;
}

.gs-pricing-editor-type-note p {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.45;
}

.gs-pricing-editor-split-input {
  display: grid;
  grid-template-columns: 1fr 150px;
  gap: 12px;
}

.gs-pricing-editor-field label em {
  color: var(--gs-muted-foreground);
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
}

.gs-pricing-editor-empty-panel {
  padding: 16px;
  background: var(--gs-surface-subtle);
  border: 1px dashed var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-pricing-editor-empty-panel strong {
  display: block;
  margin-bottom: 4px;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
  line-height: 1.35;
}

.gs-pricing-editor-empty-panel p {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.45;
}

@media (max-width: 720px) {
  .gs-pricing-step__drawer-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .gs-pricing-step__drawer-summary > span:first-child { grid-column: 1 / -1; }
  .gs-pricing-step__summary-toggle { grid-column: 1 / -1; }
  .gs-pricing-step__row { grid-template-columns: 12px minmax(0, 1fr); gap: 10px; }
  .gs-pricing-step__row-actions { grid-column: 2; justify-self: end; }
  .gs-pricing-step__detail-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .gs-pricing-step__details,
  .gs-pricing-editor-section { padding: 16px; }
  .gs-pricing-step__divider { flex-wrap: wrap; }
  .gs-pricing-editor-grid.two,
  .gs-pricing-editor-type-grid,
  .gs-pricing-editor-type-select,
  .gs-pricing-editor-split-input {
    grid-template-columns: 1fr;
  }

  .gs-pricing-step__validation button {
    grid-template-columns: 1fr;
    gap: 2px;
  }
}

.gs-pricing-step__row,
.gs-pricing-step__details,
.gs-pricing-editor-section,
.gs-pricing-editor-type-card,
.gs-pricing-editor-type-note,
.gs-pricing-step__overflow-menu {
  background: var(--gs-surface);
  border-color: var(--gs-border);
  box-shadow: var(--gs-shadow-sm);
}


.gs-pricing-step__row strong,
.gs-pricing-step__row b,
.gs-pricing-step__detail-grid strong,
.gs-pricing-step__drawer-summary strong,
.gs-pricing-editor-section__head h4,
.gs-pricing-editor-option-row strong,
.gs-pricing-editor-type-card strong,
.gs-pricing-editor-type-note strong {
  color: var(--gs-foreground) !important;
}

.gs-pricing-step__empty,
.gs-pricing-step__row,
.gs-pricing-step__row span,
.gs-pricing-step__row i,
.gs-pricing-step__details-head > span,
.gs-pricing-step__detail-grid small,
.gs-pricing-step__details p,
.gs-pricing-step__drawer-summary small,
.gs-pricing-editor-section__head p,
.gs-pricing-editor-option-row span,
.gs-pricing-editor-type-card,
.gs-pricing-editor-type-card small,
.gs-pricing-editor-type-note p {
  color: var(--gs-muted-foreground) !important;
}

.gs-pricing-step__row.is-active,
.gs-pricing-editor-type-card.is-selected {
  background: var(--gs-active-soft);
  border-color: var(--gs-active);
  box-shadow: 0 0 0 3px var(--gs-active-shadow);
}

.gs-pricing-step__details {
  border-color: var(--gs-active);
  border-top: 0;
}

.gs-pricing-editor-option-row {
  display: flex;
  gap: 16px;
}

.gs-pricing-step__drawer-summary,
.gs-pricing-editor-option-row,
.gs-pricing-step__empty {
  background: var(--gs-surface-subtle);
  border-color: var(--gs-border);
}

.gs-pricing-editor-type-card.is-selected > span,
.gs-pricing-editor-type-card.is-selected > span::after {
  border-color: var(--gs-active);
  background: var(--gs-active);
}

.gs-pricing-step input,
.gs-pricing-step textarea,
.gs-pricing-step select,
.gs-pricing-step .gd-input,
.gs-pricing-step .gd-textarea {
  font-size: 16px !important;
}
</style>
